import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  IconButton,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  Fab,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Cliente } from '../../types';
import { apiService } from '../../services/api';

interface ClienteFormData {
  nome: string;
  email: string;
  telefone: string;
  endereco: string;
  cpfCnpj: string;
}

const Clientes: React.FC = () => {
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCliente, setEditingCliente] = useState<Cliente | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const [buscandoCnpj, setBuscandoCnpj] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // React Hook Form
  const { register, handleSubmit, reset, formState: { errors }, setError, clearErrors, setValue } = useForm<ClienteFormData>({
    defaultValues: {
      nome: '',
      email: '',
      telefone: '',
      endereco: '',
      cpfCnpj: '',
    },
  });


  const showMessage = useCallback((message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  // Função para aplicar máscara de CPF/CNPJ
  const aplicarMascaraCpfCnpj = useCallback((valor: string) => {
    const apenasNumeros = valor.replace(/\D/g, '');

    if (apenasNumeros.length <= 11) {
      // Máscara de CPF: 000.000.000-00
      return apenasNumeros
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    } else {
      // Máscara de CNPJ: 00.000.000/0000-00
      return apenasNumeros
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
    }
  }, []);

  // Função para buscar dados do CNPJ
  const buscarDadosCNPJ = useCallback(async (cnpj: string) => {
    const apenasNumeros = cnpj.replace(/\D/g, '');

    // Só busca se tiver 14 dígitos (CNPJ)
    if (apenasNumeros.length !== 14) {
      return;
    }

    setBuscandoCnpj(true);
    try {
      const dados = await apiService.buscarCNPJ(apenasNumeros);

      // Preencher os campos com os dados retornados
      setValue('nome', dados.nome || '');
      setValue('email', dados.email || '');
      setValue('telefone', dados.telefone || '');

      // Montar endereço completo
      const enderecoCompleto = [
        dados.endereco?.rua,
        dados.endereco?.numero,
        dados.endereco?.bairro,
        dados.endereco?.cidade,
        dados.endereco?.estado,
        dados.endereco?.cep
      ].filter(Boolean).join(', ');

      setValue('endereco', enderecoCompleto);

      showMessage('Dados do CNPJ carregados com sucesso!', 'success');
    } catch (error: any) {
      const errorMessage = error?.message || error?.error || 'Erro ao buscar CNPJ. Verifique o número e tente novamente.';
      showMessage(errorMessage, 'error');
    } finally {
      setBuscandoCnpj(false);
    }
  }, [setValue, showMessage]);

  useEffect(() => {
    loadClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadClientes = async () => {
    try {
      setLoading(true);
      const data = await apiService.getClientes();
      setClientes(data);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      showMessage('Erro ao carregar clientes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = useCallback((cliente?: Cliente) => {
    if (cliente) {
      setEditingCliente(cliente);
      reset({
        nome: cliente.nome,
        email: cliente.email,
        telefone: cliente.telefone,
        endereco: cliente.endereco,
        cpfCnpj: cliente.cpfCnpj,
      });
    } else {
      setEditingCliente(null);
      reset({
        nome: '',
        email: '',
        telefone: '',
        endereco: '',
        cpfCnpj: '',
      });
    }
    setDialogOpen(true);
  }, [reset]);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setEditingCliente(null);
  }, []);

  const onSubmit = async (data: ClienteFormData) => {
    clearErrors();

    let hasErrors = false;

    if (!data.nome || data.nome.trim() === '') {
      setError('nome', { type: 'required', message: 'Nome é obrigatório' });
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    try {
      if (editingCliente) {
        await apiService.updateCliente(editingCliente.id, data);
        showMessage('Cliente atualizado com sucesso!', 'success');
      } else {
        await apiService.createCliente({
          ...data,
          dataCadastro: new Date().toISOString().split('T')[0],
        });
        showMessage('Cliente criado com sucesso!', 'success');
      }
      handleCloseDialog();
      loadClientes();
    } catch (error: any) {
      console.error('Erro ao salvar cliente:', error);
      const errorMessage = error?.message || error?.error || 'Erro ao salvar cliente. Verifique os dados e tente novamente.';
      showMessage(errorMessage, 'error');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este cliente?')) {
      try {
        await apiService.deleteCliente(id);
        showMessage('Cliente excluído com sucesso!', 'success');
        loadClientes();
      } catch (error: any) {
        console.error('Erro ao excluir cliente:', error);
        const errorMessage = error?.message || error?.error || 'Erro ao excluir cliente. Tente novamente.';
        showMessage(errorMessage, 'error');
      }
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'nome', headerName: 'Nome', flex: 1, minWidth: 150 },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 200 },
    { field: 'telefone', headerName: 'Telefone', width: 150 },
    { field: 'cpfCnpj', headerName: 'CPF/CNPJ', width: 150 },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleOpenDialog(params.row)}
            color="primary"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row.id)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const ClienteCard: React.FC<{ cliente: Cliente }> = ({ cliente }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="div">
            {cliente.nome}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Email: {cliente.email}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Telefone: {cliente.telefone}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          CPF/CNPJ: {cliente.cpfCnpj}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Endereço: {cliente.endereco}
        </Typography>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end' }}>
        <Button
          size="small"
          startIcon={<EditIcon />}
          onClick={() => handleOpenDialog(cliente)}
        >
          Editar
        </Button>
        <Button
          size="small"
          startIcon={<DeleteIcon />}
          color="error"
          onClick={() => handleDelete(cliente.id)}
        >
          Excluir
        </Button>
      </CardActions>
    </Card>
  );

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
        >
          Clientes
        </Typography>
        {!isMobile && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Novo Cliente
          </Button>
        )}
      </Box>

      {isMobile ? (
        <>
          <Box sx={{ mb: 2 }}>
            {loading ? (
              <Typography>Carregando...</Typography>
            ) : (
              clientes.map((cliente) => (
                <ClienteCard key={cliente.id} cliente={cliente} />
              ))
            )}
          </Box>
          <Fab
            color="primary"
            aria-label="add"
            sx={{ position: 'fixed', bottom: 16, right: 16 }}
            onClick={() => handleOpenDialog()}
          >
            <AddIcon />
          </Fab>
        </>
      ) : (
        <Paper sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={clientes}
            columns={columns}
            loading={loading}
            pageSizeOptions={[5, 10, 25]}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            disableRowSelectionOnClick
          />
        </Paper>
      )}

      {/* Dialog para criar/editar cliente */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {editingCliente ? 'Editar Cliente' : 'Novo Cliente'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" type="submit">
                  Salvar
                </Button>
                <Button variant="outlined" onClick={handleCloseDialog}>
                  Cancelar
                </Button>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                fullWidth
                label="Nome"
                required
                {...register('nome')}
                error={!!errors.nome}
                helperText={errors.nome?.message}
              />

              <TextField
                fullWidth
                label="Email"
                type="email"
                {...register('email')}
              />

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 200px', minWidth: 150 }}>
                  <TextField
                    fullWidth
                    label="Telefone"
                    {...register('telefone')}
                  />
                </Box>
                <Box sx={{ flex: '1 1 200px', minWidth: 150 }}>
                  <TextField
                    fullWidth
                    label="CPF/CNPJ"
                    {...register('cpfCnpj')}
                    onChange={(e) => {
                      const valorComMascara = aplicarMascaraCpfCnpj(e.target.value);
                      setValue('cpfCnpj', valorComMascara);
                    }}
                    onBlur={(e) => {
                      const apenasNumeros = e.target.value.replace(/\D/g, '');
                      if (apenasNumeros.length === 14) {
                        buscarDadosCNPJ(e.target.value);
                      }
                    }}
                    InputProps={{
                      endAdornment: buscandoCnpj ? (
                        <Typography variant="caption" color="primary">
                          Buscando...
                        </Typography>
                      ) : null
                    }}
                    helperText="Digite o CNPJ para buscar automaticamente"
                  />
                </Box>
              </Box>

              <TextField
                fullWidth
                label="Endereço"
                multiline
                rows={2}
                {...register('endereco')}
              />
            </Box>
          </DialogContent>
        </form>
      </Dialog>

      {/* Snackbar para mensagens de feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Clientes;
